import React, { createContext, useContext, useEffect, useState } from 'react'
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User as FirebaseUser 
} from 'firebase/auth'
import { auth, db } from '@/lib/firebase'
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'
import { User, Tenant } from '@/types'

interface AuthContextType {
  user: User | null
  firebaseUser: FirebaseUser | null
  tenant: Tenant | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  hasPermission: (resource: string, action: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [loading, setLoading] = useState(true)

  // Fetch user data from Firestore
  const fetchUserData = async (firebaseUser: FirebaseUser) => {
    try {
      // Search for user across tenants (simplified multi-tenant lookup)
      const tenantIds = ['internal', 'client', 'creator'] // Common tenant IDs
      
      for (const tenantId of tenantIds) {
        try {
          const usersRef = collection(db, 'tenants', tenantId, 'users')
          const q = query(usersRef, where('email', '==', firebaseUser.email))
          const querySnapshot = await getDocs(q)
          
          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0]
            const userData = userDoc.data() as User
            
            // Get tenant data
            const tenantRef = doc(db, 'tenants', tenantId, 'config', 'settings')
            const tenantDoc = await getDoc(tenantRef)
            
            const tenantData: Tenant = tenantDoc.exists() ? tenantDoc.data() as Tenant : {
              id: tenantId,
              name: `${tenantId} Organization`,
              type: tenantId as any,
              config: {
                features: ['content', 'analytics', 'blockchain'],
                limits: {
                  storage: 100 * 1024 * 1024 * 1024, // 100GB
                  uploads: 1000,
                  users: 50
                }
              },
              createdAt: new Date(),
              updatedAt: new Date()
            }
            
            setUser({
              ...userData,
              id: userDoc.id,
              tenantId: tenantId
            })
            setTenant(tenantData)
            return
          }
        } catch (error) {
          console.warn(`Error querying tenant ${tenantId}:`, error)
          continue
        }
      }
      
      // User not found in any tenant
      console.error('User not found in any tenant')
      setUser(null)
      setTenant(null)
    } catch (error) {
      console.error('Error fetching user data from Firestore:', error)
      setUser(null)
      setTenant(null)
    }
  }


  // Check permissions
  const hasPermission = (resource: string, action: string): boolean => {
    if (!user) return false
    
    const permission = user.permissions.find(p => p.resource === resource)
    return permission?.actions.includes(action) || false
  }

  // Login function using Firebase Authentication
  const login = async (email: string, password: string) => {
    try {
      console.log('🔐 Attempting Firebase login for:', email)
      
      // Firebase authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      console.log('✅ Firebase authentication successful')
      
      // Fetch user data from Firestore
      await fetchUserData(userCredential.user)
      
      console.log('✅ User data loaded from Firestore')
    } catch (error) {
      console.error('❌ Login error:', error)
      throw error
    }
  }

  // Logout function
  const logout = async () => {
    try {
      console.log('🔐 Signing out from Firebase')
      
      await signOut(auth)
      setUser(null)
      setTenant(null)
      setFirebaseUser(null)
      
      console.log('✅ Successfully signed out')
    } catch (error) {
      console.error('❌ Logout error:', error)
      throw error
    }
  }

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔄 Auth state changed:', firebaseUser?.email || 'signed out')
      setFirebaseUser(firebaseUser)
      
      if (firebaseUser) {
        // Fetch user data from Firestore
        await fetchUserData(firebaseUser)
      } else {
        // Clear user data when signed out
        setUser(null)
        setTenant(null)
      }
      
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const value: AuthContextType = {
    user,
    firebaseUser,
    tenant,
    loading,
    login,
    logout,
    hasPermission
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 